import { createContext, useContext, useReducer, type ReactNode, useCallback, useMemo } from 'react';

// ─── Tab Types ───

export type TabType =
  | 'layout-overview'
  | 'corridor'
  | 'corridor-items'
  | 'bin'
  | 'item'
  | 'item-locations'
  | 'missing'
  | 'assignments'
  | 'special-bins'
  | 'archive'

export interface Tab {
  id: string;
  type: TabType;
  label: string;
  icon?: string;
  params: Record<string, unknown>;
  closable: boolean;
}

export interface PanelState {
  tabs: Tab[];
  activeTabId: string | null;
}

// ─── Workspace State ───
// Each main tab has its own bottom panel state.

export interface WorkspaceState {
  main: PanelState;
  // Bottom panels keyed by main tab id
  bottomPanels: Record<string, PanelState>;
}

// ─── Actions ───

type Action =
  | { type: 'OPEN_TAB'; panel: 'main' | 'bottom'; tab: Tab }
  | { type: 'CLOSE_TAB'; panel: 'main' | 'bottom'; tabId: string }
  | { type: 'SET_ACTIVE_TAB'; panel: 'main' | 'bottom'; tabId: string }
  | { type: 'CLOSE_ALL_TABS'; panel: 'main' | 'bottom' }
  | { type: 'UPDATE_TAB_PARAMS'; panel: 'main' | 'bottom'; tabId: string; params: Record<string, unknown> }
  | { type: 'REPLACE_ACTIVE_TAB'; panel: 'main' | 'bottom'; tab: Tab };

function getBottomPanel(state: WorkspaceState): PanelState {
  const mainId = state.main.activeTabId;
  if (!mainId) return { tabs: [], activeTabId: null };
  return state.bottomPanels[mainId] || { tabs: [], activeTabId: null };
}

function setBottomPanel(state: WorkspaceState, ps: PanelState): WorkspaceState {
  const mainId = state.main.activeTabId;
  if (!mainId) return state;
  return { ...state, bottomPanels: { ...state.bottomPanels, [mainId]: ps } };
}

function panelReducer(ps: PanelState, action: Action): PanelState {
  switch (action.type) {
    case 'OPEN_TAB': {
      const existing = ps.tabs.find(t => t.id === action.tab.id);
      if (existing) return { ...ps, activeTabId: action.tab.id };
      return { tabs: [...ps.tabs, action.tab], activeTabId: action.tab.id };
    }
    case 'CLOSE_TAB': {
      const idx = ps.tabs.findIndex(t => t.id === action.tabId);
      const newTabs = ps.tabs.filter(t => t.id !== action.tabId);
      let newActive = ps.activeTabId;
      if (ps.activeTabId === action.tabId) {
        newActive = newTabs.length > 0
          ? (newTabs[Math.min(idx, newTabs.length - 1)]?.id ?? null)
          : null;
      }
      return { tabs: newTabs, activeTabId: newActive };
    }
    case 'SET_ACTIVE_TAB':
      return { ...ps, activeTabId: action.tabId };
    case 'CLOSE_ALL_TABS':
      return { tabs: [], activeTabId: null };
    case 'UPDATE_TAB_PARAMS':
      return { ...ps, tabs: ps.tabs.map(t => t.id === action.tabId ? { ...t, params: { ...t.params, ...action.params } } : t) };
    case 'REPLACE_ACTIVE_TAB': {
      if (!ps.activeTabId || ps.tabs.length === 0) {
        return { tabs: [action.tab], activeTabId: action.tab.id };
      }
      return {
        tabs: ps.tabs.map(t => t.id === ps.activeTabId ? action.tab : t),
        activeTabId: action.tab.id,
      };
    }
    default:
      return ps;
  }
}

function reducer(state: WorkspaceState, action: Action): WorkspaceState {
  const panel = 'panel' in action ? action.panel : 'main';

  if (panel === 'main') {
    const newMain = panelReducer(state.main, action);
    // If closing a main tab, clean up its bottom panel
    if (action.type === 'CLOSE_TAB') {
      const { [action.tabId]: _, ...rest } = state.bottomPanels;
      return { main: newMain, bottomPanels: rest };
    }
    return { ...state, main: newMain };
  }

  // Bottom panel: operate on the active main tab's bottom panel
  const bp = getBottomPanel(state);
  const newBp = panelReducer(bp, action);
  return setBottomPanel(state, newBp);
}

// ─── Context ───

interface WorkspaceContextValue {
  state: WorkspaceState;
  // Derived: current bottom panel for the active main tab
  bottomPanel: PanelState;
  openTab: (panel: 'main' | 'bottom', tab: Omit<Tab, 'id'> & { id?: string }) => void;
  closeTab: (panel: 'main' | 'bottom', tabId: string) => void;
  setActiveTab: (panel: 'main' | 'bottom', tabId: string) => void;
  closeAllTabs: (panel: 'main' | 'bottom') => void;
  updateTabParams: (panel: 'main' | 'bottom', tabId: string, params: Record<string, unknown>) => void;
  replaceActiveTab: (panel: 'main' | 'bottom', tab: Omit<Tab, 'id'> & { id?: string }) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const initialState: WorkspaceState = {
  main: {
    tabs: [
      { id: 'module-layout-overview', type: 'layout-overview', label: 'Layout', params: {}, closable: false },
    ],
    activeTabId: 'module-layout-overview',
  },
  bottomPanels: {},
};

let tabCounter = 0;
function genTabId(type: string): string {
  return `${type}-${++tabCounter}`;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const bottomPanel = useMemo(() => getBottomPanel(state), [state]);

  const openTab = useCallback((panel: 'main' | 'bottom', tab: Omit<Tab, 'id'> & { id?: string }) => {
    const id = tab.id || genTabId(tab.type);
    dispatch({ type: 'OPEN_TAB', panel, tab: { ...tab, id } as Tab });
  }, []);

  const closeTab = useCallback((panel: 'main' | 'bottom', tabId: string) => {
    dispatch({ type: 'CLOSE_TAB', panel, tabId });
  }, []);

  const setActiveTab = useCallback((panel: 'main' | 'bottom', tabId: string) => {
    dispatch({ type: 'SET_ACTIVE_TAB', panel, tabId });
  }, []);

  const closeAllTabs = useCallback((panel: 'main' | 'bottom') => {
    dispatch({ type: 'CLOSE_ALL_TABS', panel });
  }, []);

  const updateTabParams = useCallback((panel: 'main' | 'bottom', tabId: string, params: Record<string, unknown>) => {
    dispatch({ type: 'UPDATE_TAB_PARAMS', panel, tabId, params });
  }, []);

  const replaceActiveTab = useCallback((panel: 'main' | 'bottom', tab: Omit<Tab, 'id'> & { id?: string }) => {
    const ps = panel === 'main' ? state.main : getBottomPanel(state);
    const id = tab.id || ps.activeTabId || genTabId(tab.type);
    dispatch({ type: 'REPLACE_ACTIVE_TAB', panel, tab: { ...tab, id } as Tab });
  }, [state]);

  return (
    <WorkspaceContext.Provider value={{ state, bottomPanel, openTab, closeTab, setActiveTab, closeAllTabs, updateTabParams, replaceActiveTab }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
